import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import PlaybackLockedScreen from '../components/PlaybackLockedScreen';
import { useMusic } from '../contexts/MusicContext';
import { useRemoteBus } from '../contexts/RemoteBusContext';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { ScrollArea } from '../components/ui/scroll-area';
import { Play, Pause, Disc3, Music, Headphones, GripVertical, X, Plus, Search, SkipBack, SkipForward, ListPlus, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import RequestsInbox from '../components/RequestsInbox';
import ShoutoutsInbox from '../components/ShoutoutsInbox';
import { VibeOverrideModal } from '../components/host/VibeOverrideModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatTime = (ms) => {
  if (!ms || ms < 0) return '0:00';
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const Deck = ({ label, accent, track, state, progress, duration, onPlay, onPause, onDrop, onSeek, dropActive, autoDJ }) => {
  const [isOver, setIsOver] = useState(false);
  const isPlaying = state === 'playing';
  const pct = duration > 0 ? Math.min(100, (progress / duration) * 100) : 0;

  const handleDragOver = (e) => {
    if (autoDJ) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    if (!isOver) setIsOver(true);
  };
  const handleDragLeave = () => setIsOver(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setIsOver(false);
    if (autoDJ) return;
    const data = e.dataTransfer.getData('application/json');
    if (!data) return;
    try {
      const track = JSON.parse(data);
      onDrop(track);
    } catch (err) {
      // Drop ignored — payload wasn't our JSON track shape, normal during external drags
      console.debug('[DJConsole] drop parse failed:', err?.message);
    }
  };

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`relative rounded-2xl lg:rounded-3xl p-3 lg:p-6 border transition-all backdrop-blur-xl ${
        isOver
          ? 'border-[color:var(--accent)] bg-[color:var(--accent)]/10 scale-[1.02]'
          : 'border-white/10 bg-white/[0.03]'
      }`}
      style={{ '--accent': accent }}
      data-testid={`deck-${label.toLowerCase()}`}
      data-deck-drop-zone={label}
    >
      {/* Deck label */}
      <div className="flex items-center justify-between mb-3 lg:mb-5">
        <div className="flex items-center gap-2">
          <div
            className="w-7 h-7 lg:w-9 lg:h-9 rounded-full flex items-center justify-center shadow-lg"
            style={{ background: accent, boxShadow: `0 0 18px ${accent}55` }}
          >
            <Disc3 className={`w-4 h-4 lg:w-5 lg:h-5 text-black ${isPlaying ? 'animate-spin-slow' : ''}`} />
          </div>
          <div>
            <p className="text-[9px] lg:text-[10px] uppercase tracking-[0.2em] lg:tracking-[0.25em] text-gray-400 leading-tight">Deck</p>
            <p className="text-base lg:text-xl font-extrabold text-white tracking-wider leading-tight" style={{ fontFamily: 'Outfit' }}>{label}</p>
          </div>
        </div>
        <div className="text-right hidden sm:block">
          <p className="text-[10px] uppercase tracking-[0.25em] text-gray-400">Status</p>
          <p className="text-sm font-semibold" style={{ color: isPlaying ? accent : '#888' }}>
            {state === 'playing' ? 'LIVE' : state === 'paused' ? 'CUED' : 'EMPTY'}
          </p>
        </div>
        {/* Mobile-only compact status pill */}
        <div className="sm:hidden">
          <span
            className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
            style={{ background: `${accent}22`, color: isPlaying ? accent : '#888' }}
          >
            {state === 'playing' ? 'LIVE' : state === 'paused' ? 'CUED' : 'EMPTY'}
          </span>
        </div>
      </div>

      {/* Track art + info */}
      <div className="flex gap-2 lg:gap-4 items-center mb-3 lg:mb-5">
        <div className="relative w-14 h-14 lg:w-28 lg:h-28 rounded-xl lg:rounded-2xl overflow-hidden bg-white/5 flex-shrink-0">
          {track?.album_image ? (
            <img src={track.album_image} alt="" className={`w-full h-full object-cover ${isPlaying ? 'animate-pulse-slow' : ''}`} />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Music className="w-6 h-6 lg:w-10 lg:h-10 text-gray-700" />
            </div>
          )}
          {isPlaying && (
            <div className="absolute inset-0 ring-2 rounded-xl lg:rounded-2xl pointer-events-none" style={{ borderColor: accent, boxShadow: `inset 0 0 30px ${accent}33` }} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm lg:text-lg font-bold text-white truncate" data-testid={`deck-${label.toLowerCase()}-title`}>
            {track?.name || (autoDJ ? 'Waiting…' : 'Drag a song here')}
          </p>
          <p className="text-xs lg:text-sm text-gray-400 truncate">
            {track ? (Array.isArray(track.artists) ? track.artists.join(', ') : track.artists) : '—'}
          </p>
          <p className="text-[10px] lg:text-xs text-gray-500 mt-1 lg:mt-2 font-mono">
            {formatTime(progress)} <span className="text-gray-700">/</span> {formatTime(duration)}
          </p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="relative h-1 lg:h-1.5 bg-white/5 rounded-full overflow-hidden mb-3 lg:mb-4">
        <div
          className="absolute inset-y-0 left-0 transition-all duration-200"
          style={{ width: `${pct}%`, background: accent, boxShadow: `0 0 12px ${accent}` }}
        />
      </div>

      {/* Play / Pause */}
      <div className="flex justify-center">
        <button
          onClick={isPlaying ? onPause : onPlay}
          disabled={!track || autoDJ}
          className="w-12 h-12 lg:w-14 lg:h-14 rounded-full flex items-center justify-center transition-all disabled:opacity-30 disabled:cursor-not-allowed hover:scale-110 active:scale-95"
          style={{
            background: track && !autoDJ ? accent : '#222',
            boxShadow: track && !autoDJ ? `0 0 24px ${accent}66` : 'none',
          }}
          data-testid={`deck-${label.toLowerCase()}-toggle`}
          title={autoDJ ? 'Disable Auto DJ to control manually' : isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying
            ? <Pause className="w-5 h-5 lg:w-6 lg:h-6 text-black" fill="currentColor" />
            : <Play className="w-5 h-5 lg:w-6 lg:h-6 text-black ml-0.5" fill="currentColor" />}
        </button>
      </div>

      {/* Drop hint overlay (manual mode only) */}
      {!autoDJ && !track && (
        <div className="absolute inset-x-2 lg:inset-x-6 bottom-2 lg:bottom-6 text-center pointer-events-none">
          <p className="text-[9px] lg:text-xs text-gray-500 uppercase tracking-widest">Drop a song</p>
        </div>
      )}
    </div>
  );
};

const DJConsolePage = () => {
  const {
    autoDJ, setAutoDJ, crossfader, setCrossfader,
    slotATrack, slotBTrack, slotAState, slotBState,
    slotAProgress, slotBProgress, slotADuration, slotBDuration,
    loadSlot, playSlot, pauseSlot, seekSlot, playTracks,
    isPlaying, currentTrack, queue, queueIndex,
    pause, play, nextTrack, previousTrack, injectNext,
  } = useMusic();
  const { markHandled: markCommandHandled } = useRemoteBus();

  const [myPlaylists, setMyPlaylists] = useState([]);
  const [curatedPlaylists, setCuratedPlaylists] = useState([]);
  const [selectedKey, setSelectedKey] = useState(''); // "<type>:<id>"
  const [tracks, setTracks] = useState([]);
  const [loadingTracks, setLoadingTracks] = useState(false);
  const [activeShoutout, setActiveShoutout] = useState(null);

  // Premium playback gating — fetch on mount + on demand "recheck"
  const [playbackStatus, setPlaybackStatus] = useState({ loading: true, locked: false, reason: null });
  const fetchPlaybackStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/me/playback-status`);
      setPlaybackStatus({
        loading: false,
        locked: !!res.data?.locked,
        reason: res.data?.reason || null,
      });
    } catch (e) {
      // If the endpoint fails (e.g. super-admin viewing the page), don't lock.
      console.warn('[DJConsole] playback-status fetch failed:', e?.message || e);
      setPlaybackStatus({ loading: false, locked: false, reason: null });
    }
  }, []);
  useEffect(() => { fetchPlaybackStatus(); }, [fetchPlaybackStatus]);

  // Load all playlists for the picker
  useEffect(() => {
    (async () => {
      try {
        const [createdRes, assignedRes] = await Promise.all([
          axios.get(`${API}/playlists/created`),
          axios.get(`${API}/playlists/my-playlists`),
        ]);
        setMyPlaylists(createdRes.data || []);
        setCuratedPlaylists(assignedRes.data || []);
        const first = (createdRes.data && createdRes.data[0])
          ? `created:${createdRes.data[0].id}`
          : (assignedRes.data && assignedRes.data[0]) ? `curated:${assignedRes.data[0].id}` : '';
        if (first) setSelectedKey(first);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  // Load tracks when selection changes
  useEffect(() => {
    if (!selectedKey) { setTracks([]); return; }
    const [type, id] = selectedKey.split(':');
    setLoadingTracks(true);
    const endpoint = type === 'created'
      ? `${API}/playlists/created/${id}`
      : `${API}/playlists/curated/${id}`;
    axios.get(endpoint)
      .then(r => setTracks(r.data?.tracks || []))
      .catch(() => setTracks([]))
      .finally(() => setLoadingTracks(false));
  }, [selectedKey]);

  const allPlaylists = useMemo(() => ([
    ...myPlaylists.map(p => ({ ...p, _type: 'created' })),
    ...curatedPlaylists.map(p => ({ ...p, _type: 'curated' })),
  ]), [myPlaylists, curatedPlaylists]);

  // Auto-load playlist selection for the venue's DJ Console on first arrival.
  //   1) PRIORITY: if a playback queue is already active (we're playing
  //      something right now), select THAT playlist in the dropdown — so
  //      the console doesn't yank the DJ to a different playlist just
  //      because the scheduler thinks a different slot is current.
  //   2) FALLBACK: if no active queue, load the time-slot-scheduled playlist.
  const autoLoadedRef = useRef(false);
  useEffect(() => {
    if (autoLoadedRef.current) return;
    if (!myPlaylists.length && !curatedPlaylists.length) return;
    autoLoadedRef.current = true;
    (async () => {
      // 1) Prefer the currently-playing playlist
      try {
        const q = await axios.get(`${API}/queue/current`);
        const pid = q.data?.playlist_id;
        const ptype = q.data?.playlist_type;
        if (pid && ptype && (q.data?.tracks?.length || 0) > 0) {
          setSelectedKey(`${ptype}:${pid}`);
          return; // skip the slot lookup — we know what's actually playing
        }
      } catch (err) {
        console.debug('[DJConsole] queue/current probe failed:', err?.response?.status);
      }
      // 2) Fall back to scheduled slot playlist
      try {
        const r = await axios.get(`${API}/playlists/current-slot`);
        const pl = r.data?.playlist;
        if (pl?.id) {
          setSelectedKey(`curated:${pl.id}`);
          toast.success(`Loaded ${r.data.day} · ${r.data.slot} playlist: ${pl.name}`, { duration: 4000 });
        }
      } catch (err) {
        // No scheduled playlist for current slot — informational, not an error
        console.debug('[DJConsole] no current-slot playlist:', err?.response?.status);
      }
    })();
  }, [myPlaylists, curatedPlaylists]);

  // Remote command polling — lets the dashboard mini-player control this tab
  useEffect(() => {
    let timer;
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      try {
        const r = await axios.get(`${API}/playback/remote/poll`);
        for (const c of (r.data?.commands || [])) {
          // Item 5B — WebSocket delivery may have already handled this
          // command; skip if so. `markCommandHandled` is idempotent and
          // returns true when the id was already in the shared seen-set.
          if (c.id && markCommandHandled(c.id)) continue;
          switch (c.command) {
            case 'play':
              if (isPlaying) break;
              if (autoDJ) play && play(); else playSlot && playSlot('A');
              break;
            case 'pause':
              if (autoDJ) pause && pause(); else pauseSlot && pauseSlot('A');
              break;
            case 'next':
              if (nextTrack) nextTrack();
              break;
            case 'prev':
              if (previousTrack) previousTrack();
              break;
            case 'set_volume': {
              const v = Math.max(0, Math.min(100, parseInt(c.payload?.volume ?? 100, 10)));
              // Apple MusicKit takes 0.0–1.0 on `music.volume`. The YouTube
              // fallback engine uses `window.__sliu_setMasterVolume` (0–100).
              // Field bug (Feb 2026): only the YT branch was called, so a
              // MusicKit-driven venue's DJ Hand-off volume slider silently
              // did nothing. Apply to both engines defensively.
              try {
                const music = window.MusicKit && window.MusicKit.getInstance && window.MusicKit.getInstance();
                if (music) music.volume = v / 100;
              } catch (mkErr) {
                console.debug('[DJConsole] MusicKit volume failed:', mkErr?.message);
              }
              if (window.YT && window.__sliu_setMasterVolume) {
                window.__sliu_setMasterVolume(v);
              }
              break;
            }
            case 'load_playlist': {
              const pid = c.payload?.playlist_id;
              if (!pid) break;
              // Try curated first, then created
              try {
                const cr = await axios.get(`${API}/playlists/curated/${pid}`);
                setSelectedKey(`curated:${pid}`);
                setTracks(cr.data?.tracks || []);
                if (!autoDJ) setAutoDJ(true);
                playTracks(cr.data?.tracks || [], 0, { playlistId: pid, playlistType: 'curated' });
                toast.success(`Loaded: ${cr.data?.name || 'playlist'}`);
              } catch (curatedErr) {
                console.debug('[DJConsole] curated playlist not found, trying created:', curatedErr?.response?.status);
                try {
                  const cr = await axios.get(`${API}/playlists/created/${pid}`);
                  setSelectedKey(`created:${pid}`);
                  setTracks(cr.data?.tracks || []);
                  if (!autoDJ) setAutoDJ(true);
                  playTracks(cr.data?.tracks || [], 0, { playlistId: pid, playlistType: 'created' });
                  toast.success(`Loaded: ${cr.data?.name || 'playlist'}`);
                } catch (createdErr) {
                  console.warn('[DJConsole] load playlist failed in both sources:', createdErr);
                  toast.error('Could not load playlist');
                }
              }
              break;
            }
            default:
              break;
          }
        }
      } catch (err) {
        // Polling failures are silent — DJ Console keeps trying every 1.5s
        console.debug('[DJConsole] remote poll error:', err?.response?.status);
      } finally {
        timer = setTimeout(poll, 1500);
      }
    };
    poll();
    return () => { stopped = true; clearTimeout(timer); };
  }, [isPlaying, autoDJ, play, pause, nextTrack, previousTrack, playSlot, pauseSlot, playTracks, setAutoDJ, markCommandHandled]);

  // Push state every 3s so the dashboard mini-player stays in sync
  useEffect(() => {
    const pushState = async () => {
      try {
        const selected = allPlaylists.find(p => `${p._type}:${p.id}` === selectedKey);
        await axios.put(`${API}/playback/state`, {
          is_playing: !!isPlaying,
          current_track: currentTrack || null,
          current_index: queueIndex || 0,
          queue_size: (queue || []).length,
          volume: 100,
          playlist_id: selected?.id || null,
          playlist_name: selected?.name || null,
        });
      } catch (err) {
        // State push runs every 3s — single failures are tolerable
        console.debug('[DJConsole] state push failed:', err?.response?.status);
      }
    };
    pushState();
    const t = setInterval(pushState, 3000);
    return () => clearInterval(t);
  }, [isPlaying, currentTrack, queue, queueIndex, selectedKey, allPlaylists]);

  const handlePlayAll = async () => {
    if (!tracks.length) { toast.error('Playlist is empty'); return; }
    if (!autoDJ) setAutoDJ(true);
    const [pType, pId] = selectedKey.split(':');
    // Resume support (Feb 2026): if the backend already has this playlist
    // loaded with current_index > 0, pick up from there instead of
    // restarting at track 1. Owner refreshes tab → clicks Play → resumes.
    let startIdx = 0;
    try {
      const q = await axios.get(`${API}/queue/current`);
      if (q.data?.playlist_id === pId && (q.data?.current_index || 0) > 0) {
        const clamped = Math.min(q.data.current_index, tracks.length - 1);
        if (clamped > 0) {
          startIdx = clamped;
          toast.success(`Resuming from track ${clamped + 1}`);
        }
      }
    } catch (_) { /* if the probe fails, fall through to fresh start */ }
    playTracks(tracks, startIdx, { playlistId: pId, playlistType: pType });
    if (startIdx === 0) toast.success('Auto DJ engaged');
  };

  const handleLoadDeck = useCallback((slot, track) => {
    loadSlot(slot, track);
    toast.success(`Loaded "${track.name}" to Deck ${slot}`);
  }, [loadSlot]);

  // ===== Touch drag-and-drop (mobile) =====
  // Long-press a track row → ghost follows finger → drop on Deck A/B.
  // Vertical scroll still works during the initial 250ms hold window.
  const startTouchDrag = useCallback((track, e) => {
    if (autoDJ) return;
    const touch = e.touches?.[0];
    if (!touch) return;
    const startX = touch.clientX;
    const startY = touch.clientY;
    let started = false;
    let ghost = null;
    let currentDropZone = null;

    const beginDrag = (tx, ty) => {
      started = true;
      if (navigator.vibrate) {
        try { navigator.vibrate(20); }
        catch (err) { console.debug('[DJConsole] vibrate not supported:', err?.message); }
      }
      ghost = document.createElement('div');
      ghost.style.cssText = [
        'position: fixed',
        'pointer-events: none',
        'z-index: 9999',
        'width: 220px',
        'max-width: 70vw',
        'padding: 10px 14px',
        'border-radius: 12px',
        'background: linear-gradient(135deg, #1a1a1a, #000)',
        'border: 2px solid #FF007F',
        'box-shadow: 0 12px 40px rgba(157,0,255,0.55)',
        'color: white',
        'font-size: 12px',
        'font-weight: 600',
        'transform: translate(-50%, -50%) scale(1.02)',
        `left: ${tx}px`,
        `top: ${ty}px`,
        'white-space: nowrap',
        'overflow: hidden',
        'text-overflow: ellipsis',
      ].join(';');
      ghost.innerText = track.name;
      document.body.appendChild(ghost);
    };

    // Auto-scroll the page (or scrollable parent) when finger nears edge
    const findScroller = () => {
      const m = document.querySelector('main');
      return m && m.scrollHeight > m.clientHeight ? m : document.scrollingElement || document.documentElement;
    };
    let autoScrollTimer = null;
    const updateAutoScroll = (y) => {
      const scroller = findScroller();
      if (!scroller) return;
      const edge = 80;
      const vh = window.innerHeight;
      let dy = 0;
      if (y < edge) dy = -Math.max(2, (edge - y) / 4);
      else if (y > vh - edge) dy = Math.max(2, (y - (vh - edge)) / 4);
      if (autoScrollTimer) { clearInterval(autoScrollTimer); autoScrollTimer = null; }
      if (dy !== 0) {
        autoScrollTimer = setInterval(() => { scroller.scrollBy(0, dy); }, 16);
      }
    };
    const stopAutoScroll = () => {
      if (autoScrollTimer) { clearInterval(autoScrollTimer); autoScrollTimer = null; }
    };

    const holdTimer = setTimeout(() => beginDrag(startX, startY), 250);

    const handleMove = (ev) => {
      const t = ev.touches?.[0];
      if (!t) return;
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;

      if (!started) {
        // Cancel long-press if user moved too far (they're scrolling)
        if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
          clearTimeout(holdTimer);
          cleanup();
        }
        return;
      }

      // Drag active — block scroll and follow finger
      if (ev.cancelable) ev.preventDefault();
      ghost.style.left = `${t.clientX}px`;
      ghost.style.top = `${t.clientY}px`;

      // Auto-scroll if finger is near top or bottom edge
      updateAutoScroll(t.clientY);

      const el = document.elementFromPoint(t.clientX, t.clientY);
      const deck = el?.closest?.('[data-deck-drop-zone]');
      if (deck) {
        document.querySelectorAll('[data-deck-drop-zone]').forEach(d => d.classList.remove('touch-drop-hover'));
        deck.classList.add('touch-drop-hover');
        currentDropZone = deck.getAttribute('data-deck-drop-zone');
        ghost.style.borderColor = currentDropZone === 'A' ? '#FF007F' : '#39FF14';
        ghost.style.boxShadow = `0 12px 40px ${currentDropZone === 'A' ? '#FF007F' : '#39FF14'}88`;
      } else if (el) {
        // Clear only when pointer is over a DIFFERENT, valid element
        // (not when finger is off-viewport returning null)
        document.querySelectorAll('[data-deck-drop-zone]').forEach(d => d.classList.remove('touch-drop-hover'));
        currentDropZone = null;
        ghost.style.borderColor = '#FF007F';
      }
    };

    const handleEnd = () => {
      clearTimeout(holdTimer);
      stopAutoScroll();
      cleanup();
      if (started && currentDropZone) {
        handleLoadDeck(currentDropZone, track);
      }
    };

    const cleanup = () => {
      stopAutoScroll();
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);
      document.removeEventListener('touchcancel', handleEnd);
      document.querySelectorAll('[data-deck-drop-zone]').forEach(d => d.classList.remove('touch-drop-hover'));
      if (ghost && ghost.parentNode) ghost.parentNode.removeChild(ghost);
    };

    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleEnd);
    document.addEventListener('touchcancel', handleEnd);
  }, [autoDJ, handleLoadDeck]);

  // === Add Songs (search + add to current created playlist) ===
  const [addOpen, setAddOpen] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [addingId, setAddingId] = useState(null);

  const selectedType = selectedKey.split(':')[0] || '';
  const selectedId = selectedKey.split(':')[1] || '';
  const canEditPlaylist = selectedType === 'created';

  // Debounced search
  useEffect(() => {
    if (!addOpen) return;
    if (!searchQ.trim()) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await axios.get(`${API}/music/search`, { params: { q: searchQ.trim(), limit: 10 } });
        setSearchResults(r.data?.tracks || []);
      } catch (e) {
        toast.error('Search failed');
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [searchQ, addOpen]);

  const handleAddToPlaylist = async (newTrack) => {
    if (!canEditPlaylist) return;
    if (tracks.some(t => t.id === newTrack.id)) {
      toast.info('Already in playlist');
      return;
    }
    setAddingId(newTrack.id);
    const nextTracks = [...tracks, newTrack];
    try {
      await axios.put(`${API}/playlists/created/${selectedId}`, { tracks: nextTracks });
      setTracks(nextTracks);
      toast.success(`Added "${newTrack.name}"`);
    } catch (e) {
      toast.error('Failed to add to playlist');
    } finally {
      setAddingId(null);
    }
  };

  // === Queue Next (search + inject into live queue right after current) ===
  // Separate state from "Add Songs" because they target different stores:
  // Add Songs writes to a SAVED playlist; Queue Next mutates the LIVE queue.
  const [queueOpen, setQueueOpen] = useState(false);
  const [queueQ, setQueueQ] = useState('');
  const [queueResults, setQueueResults] = useState([]);
  const [queueSearching, setQueueSearching] = useState(false);
  const [queueAddingId, setQueueAddingId] = useState(null);

  useEffect(() => {
    if (!queueOpen) return;
    if (!queueQ.trim()) { setQueueResults([]); return; }
    const t = setTimeout(async () => {
      setQueueSearching(true);
      try {
        const r = await axios.get(`${API}/music/search`, { params: { q: queueQ.trim(), limit: 10 } });
        setQueueResults(r.data?.tracks || []);
      } catch (e) {
        toast.error('Search failed');
        setQueueResults([]);
      } finally {
        setQueueSearching(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [queueQ, queueOpen]);

  const handleQueueNext = async (newTrack) => {
    if (queue.some((t, i) => i > queueIndex && t.id === newTrack.id)) {
      toast.info('Already coming up next');
      return;
    }
    setQueueAddingId(newTrack.id);
    try {
      const res = await injectNext(newTrack);
      if (res?.play_now) {
        toast.success(`Now playing "${newTrack.name}"`);
      } else {
        toast.success(`"${newTrack.name}" queued next`);
      }
    } catch (e) {
      toast.error('Could not queue next');
    } finally {
      setQueueAddingId(null);
    }
  };

  // Crossfader value displayed
  const crossPct = Math.round(crossfader * 100);

  // Mini mode — default ON for phones (<md). Tablet+ shows the full console.
  const [miniMode, setMiniMode] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const [vibeOpen, setVibeOpen] = useState(false);

  // ---- Playback gating short-circuit ----
  // While we're checking, render nothing (avoid a flicker of the full console
  // before the lock screen appears). If locked, the entire console is replaced
  // with the lock screen (per option 2a).
  if (playbackStatus.loading) {
    return <Layout><div className="min-h-[60vh] flex items-center justify-center text-gray-500 text-sm" data-testid="dj-console-loading">Loading…</div></Layout>;
  }
  if (playbackStatus.locked) {
    return (
      <Layout>
        <PlaybackLockedScreen
          reason={playbackStatus.reason}
          onRecheck={fetchPlaybackStatus}
        />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-3 md:p-6 lg:p-8 pb-32" data-testid="dj-console-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4 mb-4 md:mb-8">
          <div className="flex items-start justify-between md:block">
            <div>
              <div className="flex items-center gap-2 md:gap-3 mb-1">
                <Headphones className="w-5 h-5 md:w-7 md:h-7 text-[#39FF14]" />
                <h1 className="text-xl md:text-3xl lg:text-4xl font-extrabold text-white tracking-tight" style={{ fontFamily: 'Outfit' }}>
                  DJ Console
                </h1>
              </div>
              <p className="text-gray-400 text-xs md:text-sm hidden md:block">Two decks. One crossfader. Total control.</p>
            </div>
            {/* Mini-mode toggle visible on small screens only */}
            <button
              type="button"
              onClick={() => setMiniMode((x) => !x)}
              className="md:hidden inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.25em] border border-white/10 text-gray-400 hover:text-white hover:border-white/30 transition-colors"
              data-testid="dj-console-mini-toggle"
            >
              {miniMode ? 'Show full' : 'Mini'}
            </button>
            {/* Mobile Vibe button (icon only) */}
            <button
              type="button"
              onClick={() => setVibeOpen(true)}
              className="md:hidden ml-1.5 inline-flex items-center justify-center w-8 h-8 rounded-full border transition-all"
              style={{
                background: 'linear-gradient(135deg, rgba(157,0,255,0.15), rgba(0,245,255,0.15))',
                borderColor: 'rgba(0, 245, 255, 0.35)',
              }}
              title="Vibe Control"
              data-testid="dj-vibe-btn-mobile"
            >
              <Sparkles className="w-3.5 h-3.5 text-[#00F5FF]" />
            </button>
          </div>

          {/* Auto DJ controls */}
          <div className="flex items-center gap-3">
            {/* Vibe Control — reshape the AI ambience filter guests are gated by. */}
            <button
              type="button"
              onClick={() => setVibeOpen(true)}
              className="hidden md:inline-flex items-center gap-2 px-4 py-3 rounded-2xl border transition-all hover:scale-105"
              style={{
                background: 'linear-gradient(135deg, rgba(157,0,255,0.08) 0%, rgba(0,245,255,0.08) 100%)',
                borderColor: 'rgba(157, 0, 255, 0.3)',
                boxShadow: '0 4px 20px -8px rgba(157, 0, 255, 0.4)',
              }}
              data-testid="dj-vibe-btn"
              title="Reshape the AI ambience filter"
            >
              <Sparkles className="w-4 h-4 text-[#00F5FF]" />
              <div className="text-left">
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#00F5FF]/80 font-['JetBrains_Mono'] leading-tight">
                  Vibe
                </p>
                <p className="text-xs font-bold text-white leading-tight">Control</p>
              </div>
            </button>

            <div className="flex items-center gap-3 bg-white/[0.04] border border-white/10 rounded-2xl px-5 py-3 backdrop-blur-xl">
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-[0.25em] text-gray-400">Auto DJ</p>
                <p className="text-sm font-bold" style={{ color: autoDJ ? '#FF007F' : '#666' }}>
                  {autoDJ ? 'ENGAGED' : 'MANUAL'}
                </p>
              </div>
              <Switch
                checked={autoDJ}
                onCheckedChange={setAutoDJ}
                className="data-[state=checked]:bg-[#FF007F]"
                data-testid="auto-dj-toggle"
              />
            </div>
          </div>
        </div>

        {/* Phone-only compact mini-player summary (visible only when miniMode is on) */}
        {miniMode && (
          <div className="md:hidden mb-4" data-testid="dj-console-mini-summary">
            <div className="bg-[#0D0D0D] border border-white/8 rounded-2xl p-4">
              <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 font-['JetBrains_Mono'] mb-3">
                Now playing
              </p>
              <div className="flex items-center gap-3 mb-4">
                {((slotATrack || slotBTrack) || {}).album_image ? (
                  <img
                    src={(slotATrack || slotBTrack).album_image}
                    alt=""
                    className="w-14 h-14 rounded-xl object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-xl bg-white/5 flex items-center justify-center flex-shrink-0">
                    <Headphones className="w-6 h-6 text-gray-600" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-white font-bold text-sm truncate font-['Outfit']">
                    {(slotATrack || slotBTrack)?.name || 'Nothing loaded'}
                  </p>
                  <p className="text-gray-400 text-xs truncate">
                    {Array.isArray((slotATrack || slotBTrack)?.artists)
                      ? (slotATrack || slotBTrack).artists.join(', ')
                      : ((slotATrack || slotBTrack)?.artists || '—')}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => previousTrack && previousTrack()}
                  className="w-12 h-12 rounded-full bg-white/5 active:bg-white/10 flex items-center justify-center"
                  data-testid="dj-mini-prev"
                  aria-label="Previous track"
                >
                  <SkipBack className="w-4 h-4 text-white" fill="white" />
                </button>
                <button
                  onClick={() => isPlaying ? (autoDJ ? pause && pause() : pauseSlot && pauseSlot('A')) : (autoDJ ? play && play() : playSlot && playSlot('A'))}
                  className="w-16 h-16 rounded-full bg-[#FF007F] active:bg-[#cc0066] flex items-center justify-center shadow-[0_8px_24px_rgba(255,0,127,0.4)]"
                  data-testid="dj-mini-play-pause"
                  aria-label={isPlaying ? 'Pause' : 'Play'}
                >
                  {isPlaying ? <Pause className="w-7 h-7 text-black" fill="black" /> : <Play className="w-7 h-7 text-black ml-0.5" fill="black" />}
                </button>
                <button
                  onClick={() => nextTrack && nextTrack()}
                  className="w-12 h-12 rounded-full bg-white/5 active:bg-white/10 flex items-center justify-center"
                  data-testid="dj-mini-next"
                  aria-label="Next track"
                >
                  <SkipForward className="w-4 h-4 text-white" fill="white" />
                </button>
              </div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-gray-600 text-center mt-3 font-['JetBrains_Mono']">
                {(queue || []).length} in queue · tap &ldquo;Show full&rdquo; for decks &amp; mixer
              </p>
            </div>

            {/* Up Next preview — visible in mini mode so the phone user can see
                what's coming without tapping "Show full". */}
            {(queue || []).length > 0 && (
              <div className="mt-4 bg-[#0D0D0D] border border-white/8 rounded-2xl p-4" data-testid="dj-console-mini-upnext">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 font-['JetBrains_Mono']">
                    Up Next
                  </p>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-gray-600 font-['JetBrains_Mono']">
                    {Math.max(0, (queue || []).length - ((queueIndex ?? -1) + 1))} song{Math.max(0, (queue || []).length - ((queueIndex ?? -1) + 1)) === 1 ? '' : 's'}
                  </p>
                </div>
                <div className="space-y-2 max-h-[55vh] overflow-y-auto hide-scrollbar -mx-1 px-1">
                  {(queue || []).slice((queueIndex ?? -1) + 1, (queueIndex ?? -1) + 31).map((t, i) => (
                    <div
                      key={`up-${t.id}-${i}`}
                      className="flex items-center gap-3 p-2 rounded-xl active:bg-white/5"
                      data-testid={`dj-mini-upnext-row-${i}`}
                    >
                      <span className="w-5 text-center text-[11px] text-gray-600 font-['JetBrains_Mono'] flex-shrink-0">
                        {i + 1}
                      </span>
                      {t.album_image ? (
                        <img
                          src={t.album_image}
                          alt=""
                          className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                          <Music className="w-4 h-4 text-gray-600" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-semibold truncate leading-tight">
                          {t.name}
                        </p>
                        <p className="text-gray-500 text-[11px] truncate leading-tight">
                          {Array.isArray(t.artists) ? t.artists.join(', ') : (t.artists || '—')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                {(queue || []).length > (queueIndex ?? -1) + 31 && (
                  <p className="text-[10px] uppercase tracking-[0.25em] text-gray-600 text-center mt-3 font-['JetBrains_Mono']">
                    + {(queue || []).length - ((queueIndex ?? -1) + 31)} more · tap &ldquo;Show full&rdquo;
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Full console sections — hidden on phone when miniMode is on */}
        <div className={miniMode ? 'hidden md:block' : 'block'}>
        {/* Guest requests + shoutouts — side-by-side on tablet/desktop, stacked on phone */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-4 mb-4 lg:mb-6">
          <RequestsInbox />
          <ShoutoutsInbox onApprove={(item) => setActiveShoutout(item)} />
        </div>
        {activeShoutout && (
          <div
            className="bg-gradient-to-r from-[#39FF14]/20 via-[#39FF14]/10 to-transparent border border-[#39FF14]/30 rounded-2xl p-3 lg:p-4 mb-4 lg:mb-6 flex items-center gap-3 lg:gap-4 backdrop-blur-xl"
            data-testid="active-shoutout-banner"
          >
            <div className="w-9 h-9 lg:w-11 lg:h-11 rounded-full bg-[#39FF14]/20 flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] text-[#39FF14] font-bold tracking-widest">LIVE</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-[#39FF14] font-bold uppercase tracking-wider">Now Shouting · {activeShoutout.guest_name}</p>
              <p className="text-white font-semibold text-sm lg:text-base truncate">{activeShoutout.message}</p>
            </div>
            <button
              onClick={() => setActiveShoutout(null)}
              className="text-gray-400 hover:text-white p-1 rounded transition-colors flex-shrink-0"
              data-testid="active-shoutout-dismiss"
              aria-label="Dismiss shoutout"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Decks row — side-by-side on all screen sizes */}
        <div className="grid grid-cols-2 gap-3 lg:gap-6 mb-4 lg:mb-6">
          <Deck
            label="A"
            accent="#FF007F"
            track={slotATrack}
            state={slotAState}
            progress={slotAProgress}
            duration={slotADuration}
            onPlay={() => playSlot('A')}
            onPause={() => pauseSlot('A')}
            onDrop={(t) => handleLoadDeck('A', t)}
            onSeek={(s) => seekSlot('A', s)}
            autoDJ={autoDJ}
          />
          <Deck
            label="B"
            accent="#39FF14"
            track={slotBTrack}
            state={slotBState}
            progress={slotBProgress}
            duration={slotBDuration}
            onPlay={() => playSlot('B')}
            onPause={() => pauseSlot('B')}
            onDrop={(t) => handleLoadDeck('B', t)}
            onSeek={(s) => seekSlot('B', s)}
            autoDJ={autoDJ}
          />
        </div>

        {/* Crossfader */}
        <div className={`bg-white/[0.03] border border-white/10 rounded-3xl p-6 mb-8 backdrop-blur-xl transition-opacity ${autoDJ ? 'opacity-40 pointer-events-none' : 'opacity-100'}`}>
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] uppercase tracking-[0.25em] text-[#FF007F] font-bold">◀ Deck A</span>
            <span className="text-[10px] uppercase tracking-[0.25em] text-gray-400 font-bold">Crossfader · {crossPct}%</span>
            <span className="text-[10px] uppercase tracking-[0.25em] text-[#39FF14] font-bold">Deck B ▶</span>
          </div>
          <div className="relative">
            <input
              type="range"
              min="0"
              max="100"
              value={crossPct}
              onChange={(e) => setCrossfader(Number(e.target.value) / 100)}
              disabled={autoDJ}
              className="crossfader-slider w-full"
              data-testid="crossfader-slider"
            />
          </div>
          <p className="text-center text-[11px] text-gray-500 mt-3">
            {autoDJ ? 'Auto DJ is engaged — crossfader is disabled.' : 'Equal-power curve · center = balanced mix'}
          </p>
        </div>

        {/* Playlist source */}
        <div className="bg-white/[0.03] border border-white/10 rounded-3xl p-4 lg:p-5 backdrop-blur-xl overflow-hidden" data-testid="dj-playlist-panel">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
            <div className="flex items-center gap-3 flex-1">
              <Music className="w-5 h-5 text-gray-400" />
              <select
                value={selectedKey}
                onChange={(e) => setSelectedKey(e.target.value)}
                className="bg-[#121212] border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF007F] min-w-[240px]"
                data-testid="dj-playlist-select"
              >
                {allPlaylists.length === 0 && <option value="">No playlists</option>}
                {myPlaylists.length > 0 && (
                  <optgroup label="My Playlists">
                    {myPlaylists.map(p => (
                      <option key={p.id} value={`created:${p.id}`}>{p.name}</option>
                    ))}
                  </optgroup>
                )}
                {curatedPlaylists.length > 0 && (
                  <optgroup label="Curated">
                    {curatedPlaylists.map(p => (
                      <option key={p.id} value={`curated:${p.id}`}>{p.name}</option>
                    ))}
                  </optgroup>
                )}
              </select>
              <span className="text-xs text-gray-500">{tracks.length} tracks</span>
            </div>
            <div className="flex items-center gap-2">
              {canEditPlaylist && (
                <Button
                  onClick={() => setAddOpen(o => !o)}
                  variant="outline"
                  className="border-white/15 text-white hover:bg-white/10 rounded-full"
                  data-testid="dj-add-songs-btn"
                >
                  {addOpen ? <X className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  {addOpen ? 'Close' : 'Add Songs'}
                </Button>
              )}
              <Button
                onClick={() => setQueueOpen(o => !o)}
                variant="outline"
                className={`rounded-full ${
                  queueOpen
                    ? 'border-[#39FF14]/40 text-[#39FF14] hover:bg-[#39FF14]/10'
                    : 'border-white/15 text-white hover:bg-white/10'
                }`}
                data-testid="dj-queue-next-btn"
                title="Add a song that plays right after the current track"
              >
                {queueOpen ? <X className="w-4 h-4 mr-2" /> : <ListPlus className="w-4 h-4 mr-2" />}
                {queueOpen ? 'Close' : 'Queue Next'}
              </Button>
              <Button
                onClick={handlePlayAll}
                disabled={tracks.length === 0}
                className="bg-[#FF007F] hover:bg-[#CC0066] text-black rounded-full font-bold"
                data-testid="dj-play-all"
              >
                <Play className="w-4 h-4 mr-2" fill="currentColor" /> Play All (Auto DJ)
              </Button>
            </div>
          </div>

          {/* Add Songs search panel */}
          {addOpen && canEditPlaylist && (
            <div className="mb-4 bg-black/40 border border-white/10 rounded-xl p-4" data-testid="dj-add-panel">
              <div className="flex items-center gap-2 mb-3">
                <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <input
                  autoFocus
                  type="text"
                  placeholder="Search Apple Music for songs to add…"
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  className="flex-1 bg-transparent text-white placeholder:text-gray-500 outline-none text-sm"
                  data-testid="dj-search-input"
                />
                {searching && <span className="text-[10px] uppercase tracking-widest text-gray-500">Searching…</span>}
              </div>
              <ScrollArea className="max-h-[40vh]">
                {searchResults.length === 0 && searchQ.trim() && !searching ? (
                  <p className="text-center py-6 text-gray-500 text-sm">No results.</p>
                ) : (
                  <div className="space-y-1 pr-2">
                    {searchResults.map((r, idx) => {
                      const already = tracks.some(t => t.id === r.id);
                      return (
                        <div
                          key={r.id}
                          className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5"
                          data-testid={`dj-search-result-${idx}`}
                        >
                          {r.album_image && <img src={r.album_image} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-white truncate">{r.name}</p>
                            <p className="text-xs text-gray-400 truncate">
                              {Array.isArray(r.artists) ? r.artists.join(', ') : r.artists}
                            </p>
                          </div>
                          <span className="text-xs text-gray-500 font-mono hidden sm:block">{formatTime(r.duration_ms)}</span>
                          <button
                            onClick={() => handleAddToPlaylist(r)}
                            disabled={already || addingId === r.id}
                            className={`px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold rounded-full transition-colors ${
                              already
                                ? 'bg-white/5 text-gray-500 cursor-not-allowed'
                                : 'bg-[#FF007F]/20 text-[#FF007F] hover:bg-[#FF007F]/30'
                            }`}
                            data-testid={`dj-search-add-${idx}`}
                          >
                            {already ? 'Added' : addingId === r.id ? '…' : <><Plus className="w-3 h-3 inline -mt-0.5 mr-1" />Add</>}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </div>
          )}

          {/* Queue Next search panel — injects into the LIVE queue right after the current track */}
          {queueOpen && (
            <div className="mb-4 bg-black/40 border border-[#39FF14]/20 rounded-xl p-4" data-testid="dj-queue-next-panel">
              <div className="flex items-center gap-2 mb-1">
                <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <input
                  autoFocus
                  type="text"
                  placeholder="Search a song to queue next…"
                  value={queueQ}
                  onChange={(e) => setQueueQ(e.target.value)}
                  className="flex-1 bg-transparent text-white placeholder:text-gray-500 outline-none text-sm"
                  data-testid="dj-queue-next-input"
                />
                {queueSearching && <span className="text-[10px] uppercase tracking-widest text-gray-500">Searching…</span>}
              </div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#39FF14]/70 mb-3 pl-6">
                ● Plays right after the current track
              </p>
              <ScrollArea className="max-h-[40vh]">
                {queueResults.length === 0 && queueQ.trim() && !queueSearching ? (
                  <p className="text-center py-6 text-gray-500 text-sm">No results.</p>
                ) : (
                  <div className="space-y-1 pr-2">
                    {queueResults.map((r, idx) => {
                      const upcomingIds = (queue || []).slice((queueIndex ?? -1) + 1).map(t => t.id);
                      const already = upcomingIds.includes(r.id);
                      return (
                        <div
                          key={r.id}
                          className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5"
                          data-testid={`dj-queue-next-result-${idx}`}
                        >
                          {r.album_image && <img src={r.album_image} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-white truncate">{r.name}</p>
                            <p className="text-xs text-gray-400 truncate">
                              {Array.isArray(r.artists) ? r.artists.join(', ') : r.artists}
                            </p>
                          </div>
                          <span className="text-xs text-gray-500 font-mono hidden sm:block">{formatTime(r.duration_ms)}</span>
                          <button
                            onClick={() => handleQueueNext(r)}
                            disabled={already || queueAddingId === r.id}
                            className={`px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold rounded-full transition-colors ${
                              already
                                ? 'bg-white/5 text-gray-500 cursor-not-allowed'
                                : 'bg-[#39FF14]/15 text-[#39FF14] hover:bg-[#39FF14]/25'
                            }`}
                            data-testid={`dj-queue-next-add-${idx}`}
                          >
                            {already ? 'In Queue' : queueAddingId === r.id ? '…' : <><ListPlus className="w-3 h-3 inline -mt-0.5 mr-1" />Play Next</>}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </div>
          )}

          <p className="text-xs text-gray-500 mb-3">
            {autoDJ
              ? 'Auto DJ will run the queue with a 10-second crossfade between tracks.'
              : 'Tap → A / → B to load, or long-press a track to drag it onto a deck.'}
          </p>

          <ScrollArea className="h-[44vh]">
            {loadingTracks ? (
              <div className="space-y-2 pr-3">
                {[1,2,3,4,5].map(i => (
                  <div key={i} className="h-14 bg-white/5 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : tracks.length === 0 ? (
              <div className="text-center py-12 text-gray-500 text-sm">
                <Music className="w-10 h-10 mx-auto mb-3 opacity-40" />
                Select a playlist with songs.
              </div>
            ) : (
              <div className="space-y-1 pr-3">
                {tracks.map((t, idx) => (
                  <div
                    key={t.id}
                    draggable={!autoDJ}
                    onDragStart={(e) => {
                      if (autoDJ) { e.preventDefault(); return; }
                      e.dataTransfer.setData('application/json', JSON.stringify(t));
                      e.dataTransfer.effectAllowed = 'copy';
                    }}
                    onTouchStart={(e) => startTouchDrag(t, e)}
                    className={`grid items-center gap-2 lg:gap-3 p-2 rounded-lg group transition-colors w-full ${
                      autoDJ ? 'hover:bg-white/5' : 'hover:bg-white/10 cursor-grab active:cursor-grabbing select-none'
                    }`}
                    style={{
                      gridTemplateColumns: autoDJ
                        ? 'auto 40px minmax(0, 1fr) auto'
                        : 'auto 40px minmax(0, 1fr) auto',
                      touchAction: autoDJ ? 'auto' : 'pan-y',
                    }}
                    data-testid={`dj-track-${idx}`}
                  >
                    <GripVertical className={`w-4 h-4 ${autoDJ ? 'text-gray-700' : 'text-gray-500'}`} />
                    {t.album_image ? (
                      <img src={t.album_image} alt="" className="w-10 h-10 rounded object-cover" />
                    ) : <div className="w-10 h-10" />}
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-white truncate">{t.name}</p>
                      <p className="text-xs text-gray-400 truncate">
                        {Array.isArray(t.artists) ? t.artists.join(', ') : t.artists}
                      </p>
                    </div>
                    {!autoDJ && (
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleLoadDeck('A', t)}
                          className="px-2 py-1.5 text-[10px] uppercase tracking-wider font-bold rounded bg-[#FF007F]/20 text-[#FF007F] hover:bg-[#FF007F]/30 active:bg-[#FF007F]/50"
                          data-testid={`dj-load-a-${idx}`}
                        >→ A</button>
                        <button
                          onClick={() => handleLoadDeck('B', t)}
                          className="px-2 py-1.5 text-[10px] uppercase tracking-wider font-bold rounded bg-[#39FF14]/20 text-[#39FF14] hover:bg-[#39FF14]/30 active:bg-[#39FF14]/50"
                          data-testid={`dj-load-b-${idx}`}
                        >→ B</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
        </div>{/* end mini-mode wrapper */}
      </div>
      <VibeOverrideModal open={vibeOpen} onClose={() => setVibeOpen(false)} />
    </Layout>
  );
};

export default DJConsolePage;
